// src/pages/LandingPage.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../components/LanguageSelector";
import { track, withAid } from "@/lib/track";

// Le quiz "Carte de transmission" est servi par le front Vision (autre domaine).
const QUIZ_URL = "https://vision.artkiv.com/quiz-collectionneur";

// Les 5 archétypes — icônes SVG et couleur reprises À L'IDENTIQUE de la Carte de
// Transmission (ArtkivVisionApp/src/questionnaire/archetypeIcons.ts + archetypes.ts).
// Le §7 du plan demande une identité « héritée de la Carte » : le visiteur doit
// reconnaître ici la carte qu'il recevra à la fin du test.
// ⚠️ Autre repo : si les tracés/couleurs changent dans archetypeIcons.ts / archetypes.ts,
// les répercuter ici. (Tracés = constantes internes, aucune entrée externe.)
const ARCHETYPE_ICON_PATHS = {
  batisseur:
    '<path d="M4 19h16"/><path d="M5 15.5l4-4 3.2 2.4L19 6"/><path d="M15.5 6H19v3.5"/>',
  passionne:
    '<path d="M12 20C7 16 4 13 4 9A3.3 3.3 0 0 1 12 7A3.3 3.3 0 0 1 20 9C20 13 17 16 12 20Z"/>',
  connecteur:
    '<circle cx="6" cy="7" r="2"/><circle cx="18" cy="7" r="2"/><circle cx="12" cy="17.5" r="2"/><path d="M8 7h8"/><path d="M7.1 8.7l3.8 7.1"/><path d="M16.9 8.7l-3.8 7.1"/>',
  gardien:
    '<path d="M12 3l7 2.5v5.2c0 4.3-3 7.4-7 9.3-4-1.9-7-5-7-9.3V5.5z"/><path d="M9 11.5l2 2 4-4"/>',
  mecene:
    '<path d="M12 21c-3.6-1.2-5.6-4.4-5.6-8.2"/><path d="M12 21c3.6-1.2 5.6-4.4 5.6-8.2"/><path d="M6.7 13.7c-1.7.1-3-.8-3.5-2.5 1.7-.5 3.2.2 3.8 1.7"/><path d="M7.3 10c-1.5-.4-2.5-1.7-2.4-3.4 1.6.2 2.8 1.3 2.9 3"/><path d="M17.3 13.7c1.7.1 3-.8 3.5-2.5-1.7-.5-3.2.2-3.8 1.7"/><path d="M16.7 10c1.5-.4 2.5-1.7 2.4-3.4-1.6.2-2.8 1.3-2.9 3"/>',
};

const ARCHETYPES = [
  { key: "batisseur", color: "#2563eb" },
  { key: "passionne", color: "#dc2626" },
  { key: "connecteur", color: "#7c3aed" },
  { key: "gardien", color: "#0f766e" },
  { key: "mecene", color: "#c9a961" },
];

// « Montrer le geste, pas la technologie » (doc ISEE §1). Icônes ligne, or bronze.
const GESTURE_ICON_PATHS = {
  addArtwork:
    '<path d="M22 18a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3l1.5-2.5h7L17 6h3a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="3.4"/>',
  scanDocs:
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M8 13h8"/><path d="M8 17h5"/>',
  documentState:
    '<path d="M12 3l7 2.5v5.2c0 4.3-3 7.4-7 9.3-4-1.9-7-5-7-9.3V5.5z"/><path d="M9.3 12l1.8 1.8L15 10"/>',
  trackValue:
    '<polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>',
  prepareFuture:
    '<rect x="3.5" y="11" width="17" height="9.5" rx="1"/><path d="M2 8.5h20V11H2z"/><path d="M12 8.5v12"/><path d="M12 8.5S9.7 4 7.3 5.4 8.6 8.5 12 8.5z"/><path d="M12 8.5s2.3-4.5 4.7-3.1S15.4 8.5 12 8.5z"/>',
  advisor:
    '<path d="M16 20v-1.6a3.4 3.4 0 0 0-3.4-3.4H6.4A3.4 3.4 0 0 0 3 18.4V20"/><circle cx="9.5" cy="8" r="3.2"/><path d="M21 20v-1.6a3.4 3.4 0 0 0-2.6-3.3"/><path d="M16 4.9a3.2 3.2 0 0 1 0 6.2"/>',
  phone:
    '<rect x="6" y="2.5" width="12" height="19" rx="2.2"/><path d="M10.5 18.5h3"/>',
};

// Narration : Rassembler → Documenter → Comprendre → Protéger → Valoriser → Transmettre.
const GESTURE_FLOW = ["gather", "document", "understand", "protect", "value", "transmit"];
// Les quatre gestes filmés. Les MP4 sont volontairement sans texte :
// titres et descriptions restent gérés par i18n dans la page.
const VIDEO_GESTURES = [
  { key: "addArtwork", file: "capture-action.mp4" },
  { key: "scanDocs", file: "document-action.mp4" },
  { key: "documentState", file: "damage-action.mp4" },
  { key: "phone", file: "scan-action.mp4" },
];

// Les usages qui restent illustrés par les pictogrammes existants.
const ICON_GESTURES = ["trackValue", "prepareFuture", "advisor"];

export default function LandingPage() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  // KPI 1 : visite de la landing. Une fois par montage (pas à chaque re-render).
  useEffect(() => { track("landing_view"); }, []);

  // Chaque lien vers le quiz porte ?aid=<anon_id> → relie visite et quiz cross-domaine.
  const quizUrl = withAid(QUIZ_URL);

  useEffect(() => {
    document.title = t("landing.meta.title");
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", t("landing.meta.description"));
  }, [t]);

  return (
    <div className="bg-white text-black min-h-screen">
      {/* NAV */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-serif text-2xl tracking-tight">ARTKIV</span>

          <nav className="flex items-center gap-4">
            <a
              href="#how"
              className="hidden md:inline text-sm text-gray-600 hover:text-black"
            >
              {t("landing.nav.how")}
            </a>
            <Link
              to="/explore"
              className="hidden md:inline text-sm text-gray-600 hover:text-black"
            >
              {t("landing.nav.explore")}
            </Link>
            <LanguageSelector />
            <Link
              to="/login"
              className="text-sm px-4 py-2 border border-black hover:bg-black hover:text-white transition-colors"
            >
              {t("landing.nav.login")}
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative w-full text-white overflow-hidden bg-black">
        <div className="absolute inset-0">
          <img
            src={`${process.env.PUBLIC_URL}/images/ui/landing_bg.jpg`}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-40"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/60 to-black/40" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-24 md:py-36">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase text-white/60 mb-6">
              {t("landing.hero.eyebrow")}
            </p>

            <h1 className="font-serif text-4xl md:text-6xl leading-tight mb-6">
              {t("landing.hero.title")}
            </h1>

            <p className="text-gray-200 text-lg md:text-xl leading-relaxed mb-10">
              {t("landing.hero.subtitle")}
            </p>

            <div className="flex flex-wrap items-center gap-4">
              {/* Pré-onboarding public : trouver son espace/plan sans compte */}
              <Link
                to="/orientation"
                className="px-7 py-3.5 rounded-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-white/60"
                style={{ background: "#c9a961", color: "#1a1a1a" }}
              >
                {t("landing.hero.findPlan", { defaultValue: "Trouver mon plan" })}
              </Link>

              <a
                href={quizUrl}
                className="px-7 py-3.5 bg-white text-black font-medium rounded-sm hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-white/60"
              >
                {t("landing.hero.quiz")}
              </a>

              <Link
                to="/explore"
                className="px-6 py-3.5 border border-white/80 text-white rounded-sm hover:bg-white hover:text-black font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-white/60"
              >
                {t("landing.hero.explore")}
              </Link>
            </div>

            <p className="text-white/60 text-sm mt-5">
              {t("landing.hero.quizNote")}
            </p>

            <p className="text-gray-300 text-sm mt-10 pt-6 border-t border-white/15 max-w-xl">
              {t("landing.hero.trust")}
            </p>
          </div>
        </div>
      </section>

      {/* CE QUE VOUS FEREZ — montrer le geste, pas la technologie (doc ISEE §1) */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-8">
            <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-5">
              {t("landing.gestures.label")}
            </p>
            <h2 className="font-serif text-3xl md:text-5xl mb-6 leading-tight">
              {t("landing.gestures.title")}
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              {t("landing.gestures.intro")}
            </p>
          </div>

          {/* Narration : Rassembler → Documenter → Comprendre → Protéger → Valoriser → Transmettre */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-12">
            {GESTURE_FLOW.map((step, i) => (
              <React.Fragment key={step}>
                <span className="font-serif text-base md:text-lg" style={{ color: "#a9843f" }}>
                  {t(`landing.gestures.flow.${step}`)}
                </span>
                {i < GESTURE_FLOW.length - 1 && (
                  <span className="text-gray-300" aria-hidden="true">→</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Démonstrations terrain — vidéos verticales muettes, sans texte ni marque. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
            {VIDEO_GESTURES.map(({ key, file }) => (
              <article
                key={key}
                className="border border-[#e5e0d8] rounded-sm overflow-hidden bg-[#f9f7f4] flex flex-col hover:border-gray-400 transition-colors"
              >
                <div className="w-full max-w-[280px] mx-auto aspect-[9/16] bg-black overflow-hidden">
                  <video
                    className="w-full h-full object-cover"
                    src={`${process.env.PUBLIC_URL}/videos/landing-actions/${file}`}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-hidden="true"
                    tabIndex={-1}
                  />
                </div>

                <div className="p-6 bg-white flex-1">
                  <h3 className="font-serif text-xl mb-2">
                    {t(`landing.gestures.cards.${key}.title`)}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-[0.95rem]">
                    {t(`landing.gestures.cards.${key}.text`)}
                  </p>
                </div>
              </article>
            ))}
          </div>

          {/* Valeur, transmission et collaboration : pictogrammes existants. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {ICON_GESTURES.map((key) => (
              <article
                key={key}
                className="border border-[#e5e0d8] rounded-sm p-7 flex flex-col hover:border-gray-400 transition-colors"
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-5"
                  style={{ backgroundColor: "rgba(201,169,97,0.12)" }}
                  aria-hidden="true"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#a9843f"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    dangerouslySetInnerHTML={{ __html: GESTURE_ICON_PATHS[key] }}
                  />
                </div>
                <h3 className="font-serif text-xl mb-2">
                  {t(`landing.gestures.cards.${key}.title`)}
                </h3>
                <p className="text-gray-600 leading-relaxed text-[0.95rem]">
                  {t(`landing.gestures.cards.${key}.text`)}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHO */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className="p-8 border border-gray-200 rounded-sm hover:border-gray-400 transition-colors"
              >
                <h3 className="font-serif text-2xl mb-4">
                  {t(`landing.section.who${n}.title`)}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t(`landing.section.who${n}.text`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LES 5 ARCHÉTYPES — Acte 1 « Identity ». Le hameçon : chacun se reconnaît
          dans un profil, puis découvre que même le meilleur a un angle mort. */}
      <section className="py-20 md:py-24 bg-[#f9f7f4] border-y border-[#e5e0d8]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-5">
              {t("landing.archetypes.label")}
            </p>
            <h2 className="font-serif text-3xl md:text-5xl mb-6 leading-tight">
              {t("landing.archetypes.title")}
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              {t("landing.archetypes.intro")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ARCHETYPES.map(({ key, color }) => (
              <article
                key={key}
                className="bg-white border border-[#e5e0d8] rounded-sm p-7 flex flex-col hover:border-gray-400 transition-colors"
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
                  style={{ backgroundColor: `${color}1a` }}
                  aria-hidden="true"
                >
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={color}
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    dangerouslySetInnerHTML={{ __html: ARCHETYPE_ICON_PATHS[key] }}
                  />
                </div>

                <h3 className="font-serif text-2xl mb-3" style={{ color }}>
                  {t(`landing.archetypes.${key}.name`)}
                </h3>

                <p className="text-gray-700 leading-relaxed mb-5 flex-1">
                  {t(`landing.archetypes.${key}.text`)}
                </p>

                <div className="pt-4 border-t border-[#e5e0d8]">
                  <p className="text-[0.7rem] tracking-[0.12em] uppercase text-gray-500 mb-2">
                    {t("landing.archetypes.riskLabel")}
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {t(`landing.archetypes.${key}.risk`)}
                  </p>
                </div>
              </article>
            ))}
          </div>

          {/* La chute : c'est elle qui transforme la curiosité en action. */}
          <div className="mt-14 max-w-3xl">
            <p className="font-serif text-2xl md:text-3xl leading-snug mb-4">
              {t("landing.archetypes.punchline")}
            </p>
            <p className="text-gray-700 leading-relaxed mb-8">
              {t("landing.archetypes.punchlineSub")}
            </p>
            <a
              href={quizUrl}
              className="inline-block px-7 py-3.5 bg-black text-white font-medium rounded-sm hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-black/40"
            >
              {t("landing.archetypes.cta")}
            </a>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-20 md:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <h2 className="font-serif text-3xl md:text-5xl mb-6">
              {t("landing.section.why.title")}
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              {t("landing.section.why.text")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                className="bg-white p-6 border border-gray-200 rounded-sm hover:border-gray-300 transition-colors"
              >
                <p className="text-gray-800 text-sm leading-relaxed">
                  {t(`landing.section.why.point${n}`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUIZ — Carte de transmission */}
      <section className="py-20 md:py-24 bg-black text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-white/50 mb-5">
                {t("landing.quiz.label")}
              </p>
              <h2 className="font-serif text-3xl md:text-5xl mb-6">
                {t("landing.quiz.title")}
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                {t("landing.quiz.text")}
              </p>
              <a
                href={quizUrl}
                className="inline-block px-7 py-3.5 bg-white text-black font-medium rounded-sm hover:bg-gray-100 transition-colors"
              >
                {t("landing.quiz.cta")}
              </a>
              <p className="text-white/50 text-sm mt-4">
                {t("landing.quiz.note")}
              </p>
            </div>

            <ul className="divide-y divide-white/15 border-y border-white/15">
              {["builder", "aficionado", "connector", "keeper", "benefactor"].map(
                (key) => (
                  <li key={key} className="py-5">
                    <div className="font-serif text-xl mb-1">
                      {t(`landing.quiz.archetype.${key}.name`)}
                    </div>
                    <div className="text-sm text-gray-400 leading-relaxed">
                      {t(`landing.quiz.archetype.${key}.text`)}
                    </div>
                  </li>
                )
              )}
            </ul>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="py-20 md:py-24 bg-white scroll-mt-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <h2 className="font-serif text-3xl md:text-5xl mb-6">
              {t("landing.section.how.title")}
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              {t("landing.section.how.text")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className="p-8 border border-gray-200 hover:border-gray-400 transition-colors"
              >
                <div className="text-xs tracking-wide uppercase text-gray-500 mb-3">
                  {t("landing.section.how.stepLabel", { n })}
                </div>
                <h3 className="font-serif text-2xl mb-3">
                  {t(`landing.section.how.step${n}.title`)}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t(`landing.section.how.step${n}.text`)}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/explore"
              className="px-6 py-3 bg-black text-white hover:bg-gray-800 transition-colors"
            >
              {t("landing.section.how.ctaPrimary")}
            </Link>
            <Link
              to="/register"
              className="px-6 py-3 border border-black text-black hover:bg-black hover:text-white transition-colors"
            >
              {t("landing.section.how.ctaSecondary")}
            </Link>
            <span className="text-sm text-gray-500">
              {t("landing.hero.trial")}
            </span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-serif text-3xl md:text-5xl mb-6">
                {t("landing.cta.title")}
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                {t("landing.cta.text")}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Link
                to="/register"
                className="px-6 py-3.5 bg-black text-white text-center rounded-sm hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-black/30"
              >
                {t("landing.cta.button.createAccount")}
              </Link>

              <a
                href={quizUrl}
                className="px-6 py-3.5 border border-black text-black text-center rounded-sm hover:bg-black hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-black/30"
              >
                {t("landing.cta.button.quiz")}
              </a>

              <p className="text-xs text-gray-500 text-center mt-1">
                {t("landing.cta.button.note")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 bg-black text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-sm text-white/80">
              {t("landing.footer.copyright", { year })}
            </div>

            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy-policy" className="hover:underline">
                {t("landing.footer.privacy")}
              </Link>
              <Link to="/cookies" className="hover:underline">
                {t("landing.footer.cookies")}
              </Link>
              <Link to="/legal" className="hover:underline">
                {t("landing.footer.legal")}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
